import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigiHostessSetup = ({ onComplete }) => {
  const getAuthToken = () => localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
  
  const [step, setStep] = useState(1);
  const [tables, setTables] = useState([]);
  const [tableRange, setTableRange] = useState({ start: '101', end: '110' });
  const [waiters, setWaiters] = useState([{ id: '', name: '' }]);
  const [assignments, setAssignments] = useState({});

  const generateTables = () => {
    const start = parseInt(tableRange.start);
    const end = parseInt(tableRange.end);
    
    if (isNaN(start) || isNaN(end) || start > end) {
      toast.error('Invalid table range');
      return;
    }
    
    const newTables = [];
    for (let i = start; i <= end; i++) {
      newTables.push(i.toString());
    }
    
    setTables(newTables);
    toast.success(`Generated ${newTables.length} tables`);
  };

  const addWaiter = () => {
    setWaiters([...waiters, { id: `w${Date.now()}`, name: '' }]);
  };

  const updateWaiter = (index, name) => {
    const updated = [...waiters];
    updated[index] = { ...updated[index], name };
    setWaiters(updated);
  };

  const removeWaiter = (index) => {
    setWaiters(waiters.filter((_, i) => i !== index));
  };

  const assignTable = (table, waiterId) => {
    setAssignments({ ...assignments, [table]: waiterId });
  };

  const handleSetup = async () => {
    // Validate
    if (tables.length === 0) {
      toast.error('Please add tables');
      return;
    }
    
    const validWaiters = waiters.filter(w => w.name.trim());
    if (validWaiters.length === 0) {
      toast.error('Please add at least one waiter');
      return;
    }
    
    try {
      // Create restaurant setup
      await axios.post(`${API}/digihostess/setup`, {
        tables,
        waiters: validWaiters
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      // If we have assignments, save them
      if (Object.keys(assignments).length > 0) {
        for (const [tableNum, waiterId] of Object.entries(assignments)) {
          // Find table ID (we'll need to fetch after setup)
          await axios.post(`${API}/digihostess/assign`, {
            table_id: tableNum,
            waiter_id: waiterId
          }, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
        }
      }
      
      toast.success('Restaurant setup complete!');
      onComplete();
    } catch (error) {
      toast.error('Setup failed');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">DigiHostess Setup</h1>
          <p className="text-slate-400">Configure your restaurant tables and staff</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 1 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
            <span className="font-semibold">1</span>
            <span>Tables</span>
          </div>
          <ArrowRight className="text-slate-600" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
            <span className="font-semibold">2</span>
            <span>Waiters</span>
          </div>
          <ArrowRight className="text-slate-600" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 3 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
            <span className="font-semibold">3</span>
            <span>Assign</span>
          </div>
        </div>

        {/* Step 1: Tables */}
        {step === 1 && (
          <Card className="p-6 bg-slate-800 border-slate-700">
            <h2 className="text-xl font-bold text-white mb-4">Step 1: Add Tables</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-300 text-sm mb-2 block">Start Table Number</label>
                  <Input
                    type="number"
                    value={tableRange.start}
                    onChange={(e) => setTableRange({ ...tableRange, start: e.target.value })}
                    className="bg-slate-700 text-white border-slate-600"
                    placeholder="101"
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-sm mb-2 block">End Table Number</label>
                  <Input
                    type="number"
                    value={tableRange.end}
                    onChange={(e) => setTableRange({ ...tableRange, end: e.target.value })}
                    className="bg-slate-700 text-white border-slate-600"
                    placeholder="120"
                  />
                </div>
              </div>
              
              <Button onClick={generateTables} className="w-full bg-blue-600 hover:bg-blue-700">
                Generate Tables
              </Button>
              
              {tables.length > 0 && (
                <div className="mt-4">
                  <p className="text-slate-300 mb-2">Tables: {tables.length}</p>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-3 bg-slate-900 rounded-lg">
                    {tables.map(t => (
                      <span key={t} className="px-3 py-1 bg-slate-700 text-slate-300 rounded-lg text-sm">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <Button
                onClick={() => setStep(2)}
                disabled={tables.length === 0}
                className="w-full bg-green-600 hover:bg-green-700 mt-6"
              >
                Next: Add Waiters
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Waiters */}
        {step === 2 && (
          <Card className="p-6 bg-slate-800 border-slate-700">
            <h2 className="text-xl font-bold text-white mb-4">Step 2: Add Waiters</h2>
            
            <div className="space-y-3">
              {waiters.map((waiter, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={waiter.name}
                    onChange={(e) => updateWaiter(index, e.target.value)}
                    placeholder="Waiter name (e.g., Sam, John, Mary)"
                    className="flex-1 bg-slate-700 text-white border-slate-600"
                  />
                  {waiters.length > 1 && (
                    <Button
                      onClick={() => removeWaiter(index)}
                      variant="destructive"
                      size="icon"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button onClick={addWaiter} variant="outline" className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Another Waiter
              </Button>
              
              <div className="flex gap-2 mt-6">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!waiters.some(w => w.name.trim())}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Next: Assign Tables
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Assignments */}
        {step === 3 && (
          <Card className="p-6 bg-slate-800 border-slate-700">
            <h2 className="text-xl font-bold text-white mb-4">Step 3: Assign Tables to Waiters</h2>
            <p className="text-slate-400 text-sm mb-4">Optional: Assign specific tables to waiters. You can skip this step.</p>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {tables.map(table => (
                <div key={table} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <span className="text-white font-semibold">Table {table}</span>
                  <select
                    value={assignments[table] || ''}
                    onChange={(e) => assignTable(table, e.target.value)}
                    className="px-3 py-2 bg-slate-600 text-white rounded-lg border-slate-500"
                  >
                    <option value="">Unassigned</option>
                    {waiters.filter(w => w.name.trim()).map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button
                onClick={handleSetup}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Complete Setup
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DigiHostessSetup;
